import { useCallback, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { getExcel } from '@/utils/request'
import { IVL_INVENTORY_EXPORT_TIMEOUT_MS } from '@/services/inventory/inventoryStatus/ivlConstants'
import { IVL_EXPORT_ERROR_MESSAGE, ivlInventoryErrorMessage, IvlInventoryUserError } from '@/services/inventory/inventoryStatus/ivlErrorMessages'

const FALLBACK_MESSAGE = IVL_EXPORT_ERROR_MESSAGE

const readErrorMessage = async (data: unknown, status?: number): Promise<string> => {
  try {
    const raw = data instanceof Blob ? await (typeof data.text === 'function' ? data.text() : new Response(data).text()) : data
    const body: unknown = typeof raw === 'string' ? JSON.parse(raw) : raw
    return ivlInventoryErrorMessage({ response: { status, data: body } }, FALLBACK_MESSAGE)
  } catch {
    return ivlInventoryErrorMessage({ response: { status } }, FALLBACK_MESSAGE)
  }
}

/** 오류 JSON은 파일로 저장하지 않는다. 실패를 호출자에게 전달해 사유 모달을 유지한다. */
export const useIvlInventoryDownload = () => {
  const [isDownloading, setIsDownloading] = useState(false)
  const busy = useRef(false)
  const download = useCallback(async (url: string): Promise<void> => {
    if (busy.current) throw new IvlInventoryUserError('엑셀을 다운로드하고 있어요. 잠시 기다려 주세요.')
    busy.current = true
    setIsDownloading(true)
    try {
      const response = await getExcel(url, { timeout: IVL_INVENTORY_EXPORT_TIMEOUT_MS })
      const contentType = String(response.headers?.['content-type'] ?? response.data.type ?? '')
      if (!/application\/(vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet|octet-stream)/i.test(contentType)) {
        throw new IvlInventoryUserError(await readErrorMessage(response.data, response.status))
      }
      const blobUrl = URL.createObjectURL(response.data)
      try {
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `재고현황_${dayjs().format('YYYYMMDDHHmmss')}.xlsx`
        link.click()
      } finally {
        URL.revokeObjectURL(blobUrl)
      }
    } catch (error) {
      if (error instanceof IvlInventoryUserError) throw error
      const response = (error as { response?: { data?: unknown; status?: number } })?.response
      const message = response ? await readErrorMessage(response.data, response.status)
        : ivlInventoryErrorMessage(error, FALLBACK_MESSAGE)
      throw new IvlInventoryUserError(message)
    } finally {
      busy.current = false
      setIsDownloading(false)
    }
  }, [])
  return { isDownloading, download }
}
